/**
 * G16 ingest UI: project Settings › Sources (what is indexed, cursors, last sync, budget, pause,
 * consent, exclusions), the Import review queue (accept all / per item / edit), and the
 * "Outside Viko" strip for the Lanes tab. Every item drills down to its source: file + line +
 * commit with the excerpt, or the session with its excerpt, or the commits.
 *
 * Text from docs and sessions is shown as quoted text (React escapes it) and never acted on.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import {
  describeProvenance,
  INGEST_KIND_LABEL,
  type IngestCategory,
  type IngestConfigRecord,
  type IngestEdits,
  type IngestProvenance,
  type IngestSourceStatus,
  type IngestStatusRecord,
} from '@viko/protocol'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useBackend } from '../lib/backend.js'
import { projectHref } from '../lib/ia.js' // ia-shell
import {
  acceptLabel,
  decideIngest,
  type IngestAction,
  type IngestEntityStore,
  type IngestView,
  type ItemView,
  loadIngest,
  parseExclusions,
  saveIngestConfig,
} from '../lib/ingest.js'
import { activePoll } from '../lib/query-client.js'
import type { Project } from '../lib/types.js'
import {
  Ago,
  Badge,
  Button,
  Empty,
  errorText,
  Field,
  Input,
  Select,
  Textarea,
  useToast,
} from './ui.js'

const ingestKey = (projectId: string) => ['ingest', projectId] as const

function useIngest(projectId: string) {
  const b = useBackend()
  const store = b.entities as unknown as IngestEntityStore | undefined
  const q = useQuery({
    queryKey: ingestKey(projectId),
    queryFn: () => loadIngest(store as IngestEntityStore, projectId),
    enabled: !!store,
    ...activePoll(60_000),
  })
  return { store, q }
}

function Locked() {
  return (
    <p role="note" className="m-0 max-w-[68ch] text-[14px] leading-5 text-text-2">
      Shown once this device has an unlocked encrypted account: sources, proposals and decisions are
      end-to-end encrypted and readable only on your devices and machines.
    </p>
  )
}

const bytes = (n: number) => `${n.toLocaleString()} bytes`

// ---------------------------------------------------------------------------------------------
// Settings › Sources
// ---------------------------------------------------------------------------------------------

function MachineStatus({ s }: { s: IngestStatusRecord }) {
  return (
    <li className="card grid gap-2 p-3">
      <div className="flex flex-wrap items-center gap-2 text-[14px]">
        <span className="font-medium">{s.machineId}</span>
        {s.paused ? <Badge tone="wait">Paused</Badge> : <Badge tone="ok">Syncing</Badge>}
        <span className="text-text-2">
          updated <Ago ts={s.updatedAt} />
        </span>
      </div>
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[14px] text-text-2">
        <dt>Docs</dt>
        <dd className="m-0">
          {s.docs.enabled
            ? `${s.docs.files.length.toLocaleString()} planning doc${s.docs.files.length === 1 ? '' : 's'}`
            : 'Off'}
          {s.docs.lastSyncAt ? (
            <>
              {' · '}
              <Ago ts={s.docs.lastSyncAt} />
            </>
          ) : null}
          {s.docs.lastError ? (
            <span className="block text-status-fail">{s.docs.lastError}</span>
          ) : null}
        </dd>
        <dt>Sessions</dt>
        <dd className="m-0">
          {s.sessions.enabled
            ? `${s.sessions.indexed.toLocaleString()} indexed · last pass read ${bytes(s.sessions.bytesRead)} (${s.sessions.cacheHits.toLocaleString()} unchanged, not re-read)`
            : 'Off (no consent)'}
          {s.sessions.lastError ? (
            <span className="block text-status-fail">{s.sessions.lastError}</span>
          ) : null}
        </dd>
        <dt>Git</dt>
        <dd className="m-0">
          {s.git.enabled
            ? `${s.git.branch ?? 'target branch'}${s.git.head ? ` at ${s.git.head.slice(0, 12)}` : ''}${s.git.intervalMs ? ` · next look in ${Math.round(s.git.intervalMs / 1000).toLocaleString()} s` : ''}`
            : 'Off'}
          {s.git.lastError ? (
            <span className="block text-status-fail">{s.git.lastError}</span>
          ) : null}
        </dd>
        <dt>LLM review</dt>
        <dd className="m-0">
          {s.llm.used.toLocaleString()} of {s.llm.budget.toLocaleString()} segments today ·{' '}
          {s.llm.cached.toLocaleString()} served from cache
        </dd>
        <dt>Signals</dt>
        <dd className="m-0">
          {s.signals.corrections.toLocaleString()} corrections ·{' '}
          {s.signals.repeatedFailures.toLocaleString()} repeated failures ·{' '}
          {s.signals.unansweredAsks.toLocaleString()} unanswered questions
          {s.signals.shareFailures ? (
            <> · {s.signals.shareFailures.toLocaleString()} files not shared</>
          ) : null}
        </dd>
      </dl>
      {s.docs.files.length ? (
        <details>
          <summary className="cursor-pointer text-[14px]">Indexed docs</summary>
          <ul className="m-0 mt-2 grid list-none gap-0.5 p-0 text-[14px]" aria-label="Indexed docs">
            {s.docs.files.map((f) => (
              <li key={f.path} className="flex gap-2 break-all">
                <code>{f.path}</code>
                <span className="text-text-3">
                  {f.items.toLocaleString()} item{f.items === 1 ? '' : 's'}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {s.sessions.cursors.length ? (
        <details>
          <summary className="cursor-pointer text-[14px]">Session cursors</summary>
          <ul
            className="m-0 mt-2 grid list-none gap-1 p-0 text-[14px]"
            aria-label="Session cursors"
          >
            {s.sessions.cursors.map((c) => (
              <li key={c.path} className="grid gap-0.5">
                <span className="flex flex-wrap items-center gap-2">
                  <Badge>{c.agent === 'claude' ? 'Claude' : 'Codex'}</Badge>
                  {c.external ? <Badge tone="accent">Outside Viko</Badge> : null}
                  <span className="font-medium">“{c.title ?? c.sessionId}”</span>
                </span>
                <span className="break-all text-text-3">
                  {c.path} · read {bytes(c.offset)} of {bytes(c.size)}
                  {c.lastReadAt ? (
                    <>
                      {' · '}
                      <Ago ts={c.lastReadAt} />
                    </>
                  ) : null}
                </span>
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </li>
  )
}

export function IngestSources({ project }: { project: Project }) {
  const { store, q } = useIngest(project.id)
  const qc = useQueryClient()
  const toast = useToast()
  const [excl, setExcl] = useState<string | undefined>()
  if (!store) return <Locked />
  const v = q.data
  const cfg = v?.config
  const save = async (mutate: (c: IngestConfigRecord) => IngestConfigRecord, done?: string) => {
    try {
      await saveIngestConfig(store, project.id, mutate)
      if (done) toast(done)
      await qc.invalidateQueries({ queryKey: ingestKey(project.id) })
    } catch (e) {
      toast(errorText(e), 'error')
    }
  }
  const parsed = excl === undefined ? undefined : parseExclusions(excl)
  return (
    <section className="grid gap-3" aria-labelledby="ps-sources">
      <h3 id="ps-sources" className="m-0 text-[15px] font-semibold">
        Sources
      </h3>
      <p className="m-0 text-[14px] text-text-2">
        Your machines read this project’s planning docs, recent agent sessions and git history to
        propose goals and tasks and keep Viko in sync with work done elsewhere. Nothing is written
        without your review; everything is end-to-end encrypted.
      </p>
      {cfg ? (
        <div className="grid gap-2">
          <label className="flex items-center gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={cfg.sources.docs.enabled}
              onChange={(e) => {
                const on = e.target.checked
                void save((c) => ({ ...c, sources: { ...c.sources, docs: { enabled: on } } }))
              }}
            />
            Planning docs (task files, status docs, plans, roadmaps, TODOs)
          </label>
          <label className="flex items-center gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={cfg.sources.sessions.enabled}
              onChange={(e) => {
                const on = e.target.checked
                void save((c) => ({
                  ...c,
                  sources: { ...c.sources, sessions: { ...c.sources.sessions, enabled: on } },
                }))
              }}
            />
            Agent sessions (Claude and Codex, including ones run outside Viko)
          </label>
          {cfg.sources.sessions.enabled ? (
            <Field label="Session window (days)">
              <Input
                type="number"
                min={1}
                max={90}
                defaultValue={cfg.sources.sessions.windowDays}
                onBlur={(e) => {
                  const n = Math.max(1, Math.min(90, Math.round(Number(e.target.value) || 14)))
                  if (n !== cfg.sources.sessions.windowDays)
                    void save((c) => ({
                      ...c,
                      sources: { ...c.sources, sessions: { ...c.sources.sessions, windowDays: n } },
                    }))
                }}
              />
            </Field>
          ) : null}
          <label className="flex items-center gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={cfg.sources.git.enabled}
              onChange={(e) => {
                const on = e.target.checked
                void save((c) => ({ ...c, sources: { ...c.sources, git: { enabled: on } } }))
              }}
            />
            Git (commits, branches and worktrees made outside Viko)
          </label>
          <label className="flex items-center gap-2 text-[14px]">
            <input
              type="checkbox"
              checked={cfg.llm.enabled}
              onChange={(e) => {
                const on = e.target.checked
                void save((c) => ({ ...c, llm: { ...c.llm, enabled: on } }))
              }}
            />
            LLM review of new session segments
          </label>
          <Field
            label="LLM review budget (segments per day)"
            hint="Only new or changed segments are reviewed; results are cached."
          >
            <Input
              type="number"
              min={0}
              max={500}
              defaultValue={cfg.llm.dailySegments}
              onBlur={(e) => {
                const n = Math.max(0, Math.min(500, Math.round(Number(e.target.value) || 0)))
                if (n !== cfg.llm.dailySegments)
                  void save((c) => ({ ...c, llm: { ...c.llm, dailySegments: n } }), 'Saved')
              }}
            />
          </Field>
          <Field
            label="Exclusions (one glob per line)"
            hint="Repo-relative, e.g. docs/archive/** — never read, never proposed."
            error={
              parsed?.bad.length ? `Not a valid path glob: ${parsed.bad.join(', ')}` : undefined
            }
          >
            <Textarea
              rows={3}
              value={excl ?? cfg.exclude.join('\n')}
              onChange={(e) => setExcl(e.target.value)}
            />
          </Field>
          <Field
            label="Trusted agent worktrees (one glob per line)"
            hint="Branches in these worktrees belong to a local agent and never show as Outside Viko. <repo> is the repo folder name. Default: <repo>/../<repo>-wt/*"
          >
            <Textarea
              rows={2}
              defaultValue={(cfg.trustedWorktreeGlobs ?? ['<repo>/../<repo>-wt/*']).join('\n')}
              onBlur={(e) => {
                const globs = e.target.value
                  .split('\n')
                  .map((l) => l.trim())
                  .filter(Boolean)
                  .slice(0, 20)
                void save((c) => ({ ...c, trustedWorktreeGlobs: globs }), 'Saved')
              }}
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            {excl !== undefined ? (
              <Button
                size="sm"
                disabled={!!parsed?.bad.length}
                onClick={() =>
                  void save(
                    (c) => ({ ...c, exclude: parsed?.ok ?? c.exclude }),
                    'Exclusions saved',
                  ).then(() => setExcl(undefined))
                }
              >
                Save exclusions
              </Button>
            ) : null}
            <Button
              size="sm"
              onClick={() =>
                void save((c) => ({ ...c, syncRequestedAt: Date.now() }), 'Sync requested')
              }
            >
              Sync now
            </Button>
            <Button
              size="sm"
              onClick={() =>
                void save((c) => ({ ...c, paused: !c.paused }), cfg.paused ? 'Resumed' : 'Paused')
              }
            >
              {cfg.paused ? 'Resume' : 'Pause'}
            </Button>
          </div>
        </div>
      ) : null}
      {v?.status.length ? (
        <ul className="m-0 grid list-none gap-2 p-0" aria-label="Sync status per machine">
          {v.status.map((s) => (
            <MachineStatus key={s.machineId} s={s} />
          ))}
        </ul>
      ) : q.isPending ? null : (
        <p className="m-0 text-[14px] text-text-3">No machine has synced this project yet.</p>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------------------------
// Import review
// ---------------------------------------------------------------------------------------------

const CATEGORY_LABEL: Record<IngestCategory, string> = {
  conflict: 'Conflicts',
  drift: 'Outside Viko',
  import: 'To import',
  insight: 'Decisions, lessons and research',
}
const STATE_LABEL = {
  pending: 'Needs review',
  accepted: 'Accepted',
  rejected: 'Rejected',
  applied: 'Synced',
  superseded: 'No longer in source',
}
const STATUS_LABEL: Record<IngestSourceStatus, string> = {
  open: 'Open',
  'in-progress': 'In progress',
  blocked: 'Blocked',
  done: 'Done',
}

function ProvenanceRef({ p }: { p: IngestProvenance }) {
  return (
    <li className="grid gap-1">
      <span className="flex flex-wrap items-center gap-2 text-[14px] text-text-2">
        <Badge>
          {p.kind === 'doc'
            ? 'Doc'
            : p.kind === 'session'
              ? 'Session'
              : p.kind === 'commit'
                ? 'Commit'
                : p.kind === 'branch'
                  ? 'Branch'
                  : 'Worktree'}
        </Badge>
        <code className="break-all">{describeProvenance(p)}</code>
        {p.kind === 'session' && p.path ? (
          <code className="break-all text-text-3">
            {p.path}
            {p.line ? `:${p.line}` : ''}
          </code>
        ) : null}
        {p.commit && p.kind !== 'doc' ? <code>{p.commit}</code> : null}
        {p.at ? <Ago ts={p.at} /> : null}
      </span>
      {p.excerpt ? (
        <blockquote
          className="m-0 whitespace-pre-wrap break-words border-l-2 border-border-subtle pl-3 text-[14px] text-text-1"
          aria-label="Source excerpt"
        >
          {p.excerpt}
        </blockquote>
      ) : null}
    </li>
  )
}

function ItemRow({
  v,
  checked,
  onCheck,
  onDecide,
  busy,
}: {
  v: ItemView
  checked: boolean
  onCheck: (on: boolean) => void
  onDecide: (action: IngestAction, edits?: IngestEdits) => void
  busy: boolean
}) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState(v.item.title)
  const [status, setStatus] = useState<IngestSourceStatus | ''>(v.item.status ?? '')
  const i = v.item
  const edits: IngestEdits | undefined =
    title !== i.title || (status && status !== i.status)
      ? {
          ...(title !== i.title && title.trim() ? { title: title.trim() } : {}),
          ...(status && status !== i.status ? { status } : {}),
        }
      : undefined
  const entityId =
    v.decision?.entityId ?? i.entityId ?? (i.match?.kind === 'task' ? i.match.id : undefined)
  const taskLink = (v.decision?.entityKind ?? i.match?.kind) === 'task' && entityId
  return (
    <li className="card grid gap-2 p-3">
      <div className="flex items-start gap-2">
        {v.state === 'pending' ? (
          <input
            type="checkbox"
            className="mt-1"
            checked={checked}
            onChange={(e) => onCheck(e.target.checked)}
            aria-label={`Select “${i.title}”`}
          />
        ) : null}
        <button
          type="button"
          className="grid flex-1 gap-1 text-left"
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          <span className="flex flex-wrap items-center gap-2 text-[14px]">
            {open ? (
              <ChevronDown size={14} aria-hidden="true" />
            ) : (
              <ChevronRight size={14} aria-hidden="true" />
            )}
            <span className="font-medium">{i.title}</span>
          </span>
          <span className="flex flex-wrap items-center gap-2 text-[14px] text-text-2">
            <Badge
              tone={
                v.state === 'pending'
                  ? 'wait'
                  : v.state === 'accepted' || v.state === 'applied'
                    ? 'ok'
                    : undefined
              }
            >
              {STATE_LABEL[v.state]}
            </Badge>
            <Badge>{INGEST_KIND_LABEL[i.kind]}</Badge>
            {i.status ? <Badge>{STATUS_LABEL[i.status]}</Badge> : null}
            <span>
              {Math.round(i.confidence * 100)}% confidence ·{' '}
              {describeProvenance(i.provenance[0] as IngestProvenance)}
              {i.provenance.length > 1
                ? ` +${(i.provenance.length - 1).toLocaleString()} more`
                : ''}
            </span>
            {i.match?.relation === 'duplicate' ? <span>Similar to “{i.match.title}”</span> : null}
          </span>
        </button>
      </div>
      {open ? (
        <div className="grid gap-2">
          {i.detail ? (
            <p className="m-0 whitespace-pre-wrap text-[14px] text-text-2">{i.detail}</p>
          ) : null}
          <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[14px] text-text-2">
            {i.parent ? (
              <>
                <dt>Under</dt>
                <dd className="m-0">{i.parent}</dd>
              </>
            ) : null}
            {i.owner ? (
              <>
                <dt>Owner</dt>
                <dd className="m-0">{i.owner}</dd>
              </>
            ) : null}
            <dt>Seen by</dt>
            <dd className="m-0">
              {i.machineId} · <Ago ts={i.updatedAt} />
            </dd>
          </dl>
          {i.conflicts?.length ? (
            <table className="text-[14px]" aria-label="Conflicting fields">
              <thead>
                <tr>
                  <th className="text-left">Field</th>
                  <th className="text-left">Source says</th>
                  <th className="text-left">Viko has</th>
                </tr>
              </thead>
              <tbody>
                {i.conflicts.map((c) => (
                  <tr key={c.field}>
                    <td>{c.field}</td>
                    <td>{c.source}</td>
                    <td>{c.viko}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
          {taskLink ? (
            <Link
              to="/t/$taskId"
              params={{ taskId: entityId as string }}
              className="text-[14px] text-accent-text"
            >
              Open task
            </Link>
          ) : null}
          <ul className="m-0 grid list-none gap-2 p-0" aria-label="Where this came from">
            {i.provenance.map((p, n) => (
              <ProvenanceRef
                key={`${p.kind}:${p.path ?? p.commit ?? p.branch}:${p.line ?? n}`}
                p={p}
              />
            ))}
          </ul>
          {v.state === 'pending' ? (
            <div className="grid gap-2">
              {i.category !== 'conflict' && i.match?.relation !== 'completes' ? (
                <div className="flex flex-wrap gap-2">
                  <Field label="Title" className="min-w-[240px] flex-1">
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} />
                  </Field>
                  {i.kind === 'task' || i.kind === 'followup' || i.kind === 'blocker' ? (
                    <Field label="Status">
                      <Select
                        value={status}
                        onChange={(e) => setStatus(e.target.value as IngestSourceStatus)}
                      >
                        <option value="">As in source</option>
                        {(Object.keys(STATUS_LABEL) as IngestSourceStatus[]).map((s) => (
                          <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                </div>
              ) : null}
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="primary"
                  disabled={busy}
                  onClick={() => onDecide('accept', edits)}
                >
                  {acceptLabel(v, edits)}
                </Button>
                <Button
                  size="sm"
                  variant="danger"
                  disabled={busy}
                  onClick={() => onDecide('reject')}
                >
                  {i.category === 'conflict' ? 'Keep Viko version' : 'Reject'}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

type Filter = 'pending' | 'decided' | 'all'

export function ImportReview({ project }: { project: Project }) {
  const { store, q } = useIngest(project.id)
  const qc = useQueryClient()
  const toast = useToast()
  const [filter, setFilter] = useState<Filter>('pending')
  const [sel, setSel] = useState<Set<string>>(new Set())
  const [busy, setBusy] = useState(false)
  const rows = q.data?.items ?? []
  const counts = useMemo(
    () => ({
      pending: rows.filter((r) => r.state === 'pending').length,
      decided: rows.filter((r) => r.state !== 'pending').length,
      all: rows.length,
    }),
    [rows],
  )
  if (!store) return <Locked />
  if (q.isPending) return null
  if (q.isError) return <Empty title="Import review could not be read">{errorText(q.error)}</Empty>
  const shown = rows.filter((r) =>
    filter === 'all' ? true : filter === 'pending' ? r.state === 'pending' : r.state !== 'pending',
  )
  const decide = async (
    items: ItemView[],
    action: IngestAction,
    edits: Record<string, IngestEdits> = {},
  ) => {
    setBusy(true)
    try {
      const r = await decideIngest(store, project.id, items, action, edits)
      toast(
        `${r.decided.length.toLocaleString()} ${action === 'accept' ? 'accepted' : 'rejected'}${r.skipped.length ? ` · ${r.skipped.length.toLocaleString()} already decided` : ''}`,
      )
      setSel(new Set())
      await qc.invalidateQueries({ queryKey: ingestKey(project.id) })
      await qc.invalidateQueries({ queryKey: ['tasks'] })
    } catch (e) {
      toast(errorText(e), 'error')
    } finally {
      setBusy(false)
    }
  }
  const chosen = rows.filter((r) => sel.has(r.id))
  const pendingShown = shown.filter((r) => r.state === 'pending')
  const groups = (Object.keys(CATEGORY_LABEL) as IngestCategory[])
    .map((c) => ({ c, items: shown.filter((r) => r.item.category === c) }))
    .filter((g) => g.items.length)
  return (
    <section className="grid gap-3 p-4" aria-label="Import review">
      <div className="flex flex-wrap items-center gap-2">
        <div className="choice" role="radiogroup" aria-label="Show">
          {(
            [
              ['pending', 'Needs review'],
              ['decided', 'Decided'],
              ['all', 'All'],
            ] as const
          ).map(([f, label]) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={filter === f}
              onClick={() => setFilter(f)}
            >
              {label} ({counts[f].toLocaleString()})
            </button>
          ))}
        </div>
        {pendingShown.length ? (
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => void decide(pendingShown, 'accept')}
          >
            Accept all {pendingShown.length.toLocaleString()}
          </Button>
        ) : null}
      </div>
      {chosen.length ? (
        <div
          className="flex flex-wrap items-center gap-2"
          role="toolbar"
          aria-label="Batch actions"
        >
          <span className="text-[14px] text-text-2">{chosen.length.toLocaleString()} selected</span>
          <Button
            size="sm"
            variant="primary"
            disabled={busy}
            onClick={() => void decide(chosen, 'accept')}
          >
            Accept {chosen.length.toLocaleString()}
          </Button>
          <Button
            size="sm"
            variant="danger"
            disabled={busy}
            onClick={() => void decide(chosen, 'reject')}
          >
            Reject {chosen.length.toLocaleString()}
          </Button>
        </div>
      ) : null}
      {groups.length ? (
        groups.map((g) => (
          <section key={g.c} className="grid gap-2" aria-label={CATEGORY_LABEL[g.c]}>
            <h3 className="m-0 text-[14px] font-semibold">
              {CATEGORY_LABEL[g.c]} ({g.items.length.toLocaleString()})
            </h3>
            <ul className="m-0 grid list-none gap-2 p-0">
              {g.items.map((v) => (
                <ItemRow
                  key={v.id}
                  v={v}
                  busy={busy}
                  checked={sel.has(v.id)}
                  onCheck={(on) =>
                    setSel((prev) => {
                      const next = new Set(prev)
                      if (on) next.add(v.id)
                      else next.delete(v.id)
                      return next
                    })
                  }
                  onDecide={(action, edits) =>
                    void decide([v], action, edits ? { [v.id]: edits } : {})
                  }
                />
              ))}
            </ul>
          </section>
        ))
      ) : (
        <Empty title={filter === 'pending' ? 'Nothing to review' : 'Nothing imported yet'}>
          Turn on sources in Settings › Sources. Your machines propose goals and tasks from the
          project’s docs and agent sessions, and flag work that happened outside Viko.
        </Empty>
      )}
    </section>
  )
}

// ---------------------------------------------------------------------------------------------
// Lanes: work that happened outside Viko
// ---------------------------------------------------------------------------------------------

/**
 * One row for the Lanes tab: pending outside work (branches, worktrees, conflicts) and the
 * import queue, drillable to Import review. Applied or resolved drift is history, not a row.
 */
export function OutsideViko({ projectId }: { projectId: string }) {
  const { store, q } = useIngest(projectId)
  if (!store || !q.data) return null
  const v: IngestView = q.data
  const pendingItems = v.items.filter((i) => i.state === 'pending')
  const drift = pendingItems.filter(
    (i) => i.item.category === 'drift' || i.item.category === 'conflict',
  )
  const imports = pendingItems.length - drift.length
  const conflicts = drift.filter((i) => i.item.category === 'conflict').length
  const branches = drift.filter(
    (i) => i.item.key.startsWith('git:branch:') || i.item.key.startsWith('git:worktree:'),
  ).length
  const other = drift.length - conflicts - branches
  const parts = [
    branches ? `${branches.toLocaleString()} branch${branches === 1 ? '' : 'es'} to review` : '',
    conflicts ? `${conflicts.toLocaleString()} conflict${conflicts === 1 ? '' : 's'}` : '',
    other ? `${other.toLocaleString()} outside item${other === 1 ? '' : 's'}` : '',
  ].filter(Boolean)
  if (!drift.length && !imports) return null
  const label = drift.length ? `Outside Viko: ${parts.join(', ')}` : 'Import review'
  return (
    <section className="px-4 py-2" aria-label="Outside Viko" data-testid="outside-viko">
      <Link
        to={projectHref(projectId, 'history')}
        className="flex flex-wrap items-center gap-2 text-[14px] text-text-1 no-underline"
      >
        <Badge tone={conflicts ? 'fail' : 'accent'}>{drift.length ? 'Outside' : 'Import'}</Badge>
        <span>{label}</span>
        {imports ? (
          <span className="text-text-3">
            {drift.length ? '· ' : ''}
            {imports.toLocaleString()} import item{imports === 1 ? '' : 's'}
          </span>
        ) : null}
        <span aria-hidden="true" className="text-accent-text">
          ▸
        </span>
      </Link>
    </section>
  )
}
